'use client';
import { fleetApiGo } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { assertApiSuccess } from '../utils/response';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const revalidateWorkshopList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/workshop/search');
};

const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const resp = await fleetApiGo.post(url, payload);
  return resp.data;
};

export const WORKSHOP_SEARCH_FIELDS = {
  name: { name: 'name', label: 'Workshop Name' },
  cp_name: { name: 'cp_name', label: 'Contact Person' },
  cp_number: { name: 'cp_number', label: 'Contact Number' },
  address: { name: 'address', label: 'Address' }
};

const EMPTY_WORKSHOP_COLUMN = { logic_operator: 'like', name: 'name', operator: 'AND', value: '' };

export const buildWorkshopSearchPayload = ({
  start = 0,
  length = 25,
  searchKey = 'all',
  searchValue = '',
  selectedCompanyIds = ''
} = {}) => {
  const fb = new FilterBuilder().setPaging(start, length).addJoin('company', ['name']).setOrder(['name'], true);

  const companyIdsStr = selectedCompanyIds ? String(selectedCompanyIds) : '';
  if (companyIdsStr && companyIdsStr !== '') {
    if (companyIdsStr.includes(',')) {
      fb.in('company_id', companyIdsStr);
    } else {
      fb.equal('company_id', companyIdsStr);
    }
  } else {
    fb.filterByCompany();
  }

  const query = String(searchValue ?? '').trim();
  if (query) {
    if (!searchKey || searchKey === 'all') {
      const seen = new Set();
      Object.values(WORKSHOP_SEARCH_FIELDS).forEach((f) => {
        const key = `${f.name}-${f.table_name ?? ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          fb.like(f.name, query, 'OR', f.table_name ?? null);
        }
      });
    } else {
      const f = WORKSHOP_SEARCH_FIELDS[searchKey] || { name: searchKey };
      fb.like(f.name, query, 'AND', f.table_name ?? null);
    }
  }

  const built = fb.build();
  if (!built.columns || built.columns.length === 0) {
    built.columns = [{ ...EMPTY_WORKSHOP_COLUMN }];
  }
  return built;
};

export const fetchAllWorkshop = async ({ searchKey = 'all', searchValue = '', selectedCompanyIds = '', total = 0 } = {}) => {
  const payload = buildWorkshopSearchPayload({
    start: 0,
    length: total > 0 ? total : 9999,
    searchKey,
    searchValue,
    selectedCompanyIds
  });
  const resp = await fleetApiGo.post('/workshop/search' + getTzParam(), payload);
  const data = resp?.data;
  const candidate = data?.data ?? data;
  return Array.isArray(candidate) ? candidate : [];
};

export const useWorkshopList = (page = 0, rowsPerPage = 25, searchKey = 'all', searchValue = '', selectedCompanyIds = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(
    () => buildWorkshopSearchPayload({ start, length: rowsPerPage, searchKey, searchValue, selectedCompanyIds }),
    [start, rowsPerPage, searchKey, searchValue, selectedCompanyIds]
  );

  const key = useMemo(() => ['/workshop/search', JSON.stringify(payload)], [payload]);

  const {
    data: raw,
    error,
    isLoading
  } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  let rows = [];
  let total = 0;

  if (!raw) {
    rows = [];
    total = 0;
  } else {
    const candidate = raw?.data ?? raw;
    const nested = candidate?.data ?? candidate;
    if (Array.isArray(nested)) {
      rows = nested;
      total =
        Number(candidate?.totalRecord ?? candidate?.totalRecords ?? raw?.totalRecord ?? raw?.totalRecords ?? nested.length) ||
        nested.length;
    } else if (Array.isArray(raw)) {
      rows = raw;
      total = raw.length;
    } else {
      const maybeArray = raw?.data ?? null;
      if (Array.isArray(maybeArray)) {
        rows = maybeArray;
        total = Number(raw?.totalRecord ?? raw?.totalRecords ?? maybeArray.length) || maybeArray.length;
      } else {
        rows = [];
        total = 0;
      }
    }
  }

  return {
    data: rows,
    totalRecord: total,
    isLoading: isLoading || (!raw && !error),
    isError: error
  };
};

export const getWorkshopById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const resp = await fleetApiGo.post(`/workshop/search${getTzParam()}`, payload);
    const dataArr = resp?.data?.data ?? resp?.data ?? (Array.isArray(resp) ? resp : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (error) {
    console.error('Failed to fetch workshop by ID:', error);
    throw error;
  }
};

export const createWorkshop = async (formData = {}) => {
  try {
    const body = { ...formData };
    const resp = await fleetApiGo.post(`/workshop/new${getTzParam()}`, body);
    assertApiSuccess(resp, 'Create workshop failed');
    revalidateWorkshopList();
    return resp.data;
  } catch (error) {
    console.error('Error creating workshop:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateWorkshop = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const resp = await fleetApiGo.put(`/workshop/edit/${id}${getTzParam()}`, body);
    assertApiSuccess(resp, 'Update workshop failed');
    revalidateWorkshopList();
    return resp.data;
  } catch (error) {
    console.error('Error updating workshop:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteWorkshop = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApiGo.delete(`/workshop/delete/${id}${getTzParam()}`);
    revalidateWorkshopList();
    return resp.data;
  } catch (error) {
    console.error('Error deleting workshop:', error?.response?.data ?? error);
    throw error;
  }
};
